/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import Autocomplete from '@mui/material/Autocomplete';

import { Box, CircularProgress, InputAdornment, Stack, Switch, alpha } from '@mui/material';
import { useTheme } from '../../styles';
import { ModelListItem } from './ListItem/ModelListItem';
import {
  AutocompleteContainer,
  AutocompleteInput,
  AutocompletePopper,
  AutocompletePopperPortal,
  isToolModel,
} from './Base';
import { InfoOutlined } from '@mui/icons-material';
import { Tooltip } from '..';
import { useDispatch, useSelector } from 'react-redux';
import { Command, tabActions } from '@store/slices';
import { State } from '@store/index';
import { useParams } from 'react-router-dom';

const SwitchContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-left: 4px;

  p {
    color: ${({ theme }) => theme.text.subtext};
    font-size: 13px;
    margin: 0;
  }

  svg {
    color: ${({ theme }) => alpha(theme.text.standard, 0.5)};
    margin: 0;
    font-size: 16px;

    &:hover {
      cursor: pointer;
      color: ${({ theme }) => theme.text.standard};
    }
  }
`;

export const ErrorMessage = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.telnyx.error} !important;
  margin-left: 6px;
`;

export const jsonModels = ['openai/gpt-3.5-turbo-1106', 'openai/gpt-4-1106-preview'];

interface Props {
  value: string;
  onChange: (event: React.SyntheticEvent<Element, Event>, newValue: any | null) => void;
  disabled?: boolean;
  loading?: boolean;
  labels: any[];
  placeholder?: string;
  command: Command;
  portal?: boolean;
  showAdditionalToggles?: boolean;
}

export const Models: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  labels,
  placeholder,
  loading,
  command,
  portal,
  showAdditionalToggles = true,
}) => {
  const dispatch = useDispatch();
  const autocompleteRef = useRef(null);
  const theme = useTheme();

  const { workflow_id } = useParams();

  const nodeErrors = useSelector((state: State) => state.tabs.active.nodeErrors);
  const model = Boolean(command.state?.model);
  const jsonEnabled = command.state?.json_mode?.enabled;
  const showJsonMode = labels.find((item) => item?.value === value)?.metadata?.json || false;

  const [open, setOpen] = useState<boolean>(false);

  const error = nodeErrors?.find(
    (nodeError) =>
      nodeError.id === command.node.id &&
      nodeError?.workflowId === workflow_id &&
      nodeError?.fields?.find((nodeField) => nodeField.id === 'model')
  );
  const errorField = error ? error?.fields?.find((nodeField) => nodeField.id === 'model') : undefined;
  const errorFieldMessage = errorField ? errorField.message : undefined;

  const toggleJsonMode = (newVal: boolean) => {
    dispatch(tabActions.setSaved(false));

    const isEnabled = Boolean(newVal);

    // remove any tools attached to the node if enabling
    if (isEnabled) {
      dispatch(tabActions.removePluginConnections(command.node.id));
    }

    dispatch(tabActions.refreshDynamicNodeData({ node_id: command.node.id, output: null }));

    dispatch(
      tabActions.nodeInputNestedUpdate({
        nodeId: command.node.id,
        fieldId: 'json_mode',
        nestedFieldName: 'enabled',
        value: newVal,
      })
    );
  };

  return (
    <>
      <AutocompleteContainer open={open}>
        <Autocomplete
          value={labels.find((item) => item?.value === value) || null}
          ref={autocompleteRef}
          onChange={(event, newValue) => {
            onChange(event, newValue || { value: '', label: '' });
            setOpen(false);

            const doesNewModelSupportsJson =
              labels.find((item) => item?.value === newValue?.value)?.metadata?.json || false;

            if (!doesNewModelSupportsJson || !showJsonMode || !jsonEnabled) toggleJsonMode(false);

            if (!isToolModel(newValue?.value)) {
              dispatch(tabActions.removePluginConnections(command.node.id));
            }

            if (newValue) (document?.activeElement as HTMLElement)?.blur();
          }}
          disabled={disabled}
          PopperComponent={portal ? AutocompletePopperPortal : AutocompletePopper}
          groupBy={(option) => option?.metadata?.organization}
          renderOption={(props, option) => <ModelListItem {...props} option={option} />}
          autoHighlight
          options={labels}
          disablePortal
          getOptionLabel={(option) => option?.label || ''}
          renderInput={(params) => (
            <AutocompleteInput
              open={open}
              ref={params.InputProps.ref}
              inputProps={params.inputProps}
              placeholder={placeholder}
              disabled={disabled}
              onFocus={() => setOpen(true)}
              error={Boolean(errorFieldMessage)}
              onBlur={() => setOpen(false)}
              endAdornment={loading ? <CircularProgress sx={{ color: theme.primary }} size={14} /> : null}
              startAdornment={
                value &&
                !loading && (
                  <InputAdornment position='start'>
                    <Box
                      component='span'
                      sx={{
                        width: 12,
                        height: 12,
                        flexShrink: 0,
                        borderRadius: '3px',
                        mr: 0.35,
                        ml: 0.31,
                        mt: '0px',
                      }}
                      style={{ backgroundColor: labels.find((label) => label?.value === value)?.metadata?.color }}
                    />
                  </InputAdornment>
                )
              }
            />
          )}
        />
      </AutocompleteContainer>
      {errorFieldMessage && <ErrorMessage>{errorFieldMessage}</ErrorMessage>}
      {model && showJsonMode && showAdditionalToggles && (
        <SwitchContainer>
          {showJsonMode && showAdditionalToggles && (
            <Stack alignItems='center' spacing={0.5} direction='row'>
              <p>JSON Mode</p>
              <Switch size='small' checked={jsonEnabled} onChange={(e) => toggleJsonMode(e.target.checked)} />
              <Tooltip
                sx={{ maxWidth: '300px' }}
                placement='top'
                arrow
                title='If enabled, this node will output a LLM generated JSON object. The system message is not used in JSON mode.'
              >
                <InfoOutlined />
              </Tooltip>
            </Stack>
          )}
        </SwitchContainer>
      )}
    </>
  );
};
